import {
    JeopardyBoardConfig,
    JeopardyCategory,
    JeopardyClue,
} from './jeopardy-types';

const MIN_BOARD_SIZE = 2;
const MAX_BOARD_SIZE = 8;

function validateCategoryContent(
    categories: JeopardyCategory[],
    rowsCount: number,
    stagePrefix: string,
    errors: string[]
) {
    categories.forEach((cat, ci) => {
        if (!cat.name?.trim()) {
            errors.push(`${stagePrefix} Category ${ci + 1}: Name required.`);
        }

        if (!Array.isArray(cat.clues)) {
            errors.push(`${stagePrefix} Category ${ci + 1}: Clues are missing.`);
            return;
        }

        if (cat.clues.length !== rowsCount) {
            errors.push(
                `${stagePrefix} Category ${ci + 1}: Expected ${rowsCount} clues, found ${cat.clues.length}.`
            );
        }

        cat.clues.forEach((clue: JeopardyClue) => {
            if (!clue.question?.trim()) {
                errors.push(`[${cat.name || `Col ${ci + 1}`} $${clue.points}]: Question empty.`);
            }
            if (!clue.answer?.trim()) {
                errors.push(`[${cat.name || `Col ${ci + 1}`} $${clue.points}]: Answer empty.`);
            }
        });
    });
}

export function getJeopardyBoardConfigValidationErrors(config: JeopardyBoardConfig | null): string[] {
    if (!config) {
        return ['Jeopardy board configuration is missing.'];
    }

    const errors: string[] = [];

    if (config.columnsCount < MIN_BOARD_SIZE || config.columnsCount > MAX_BOARD_SIZE) {
        errors.push(
            `Columns count must be between ${MIN_BOARD_SIZE} and ${MAX_BOARD_SIZE}.`
        );
    }

    if (config.rowsCount < MIN_BOARD_SIZE || config.rowsCount > MAX_BOARD_SIZE) {
        errors.push(`Rows count must be between ${MIN_BOARD_SIZE} and ${MAX_BOARD_SIZE}.`);
    }

    if (!Array.isArray(config.jeopardyCategories)) {
        errors.push('Jeopardy stage categories are missing.');
    } else if (config.jeopardyCategories.length !== config.columnsCount) {
        errors.push(
            `Jeopardy stage must have ${config.columnsCount} categories, found ${config.jeopardyCategories.length}.`
        );
    }

    validateCategoryContent(config.jeopardyCategories ?? [], config.rowsCount, 'Jeopardy', errors);

    if (config.hasDoubleJeopardy) {
        if (!Array.isArray(config.doubleJeopardyCategories)) {
            errors.push('Double Jeopardy categories are missing.');
        } else if (config.doubleJeopardyCategories.length !== config.columnsCount) {
            errors.push(
                `Double Jeopardy stage must have ${config.columnsCount} categories, found ${config.doubleJeopardyCategories.length}.`
            );
        }

        validateCategoryContent(
            config.doubleJeopardyCategories ?? [],
            config.rowsCount,
            'Double Jeopardy',
            errors
        );
    }

    if (!config.finalJeopardy?.category?.trim()) {
        errors.push('Final Jeopardy: Category required.');
    }
    if (!config.finalJeopardy?.question?.trim()) {
        errors.push('Final Jeopardy: Question required.');
    }
    if (!config.finalJeopardy?.answer?.trim()) {
        errors.push('Final Jeopardy: Answer required.');
    }

    const allClueIds = new Set<string>();
    config.jeopardyCategories?.forEach(cat => cat.clues.forEach(clue => allClueIds.add(clue.id)));
    config.doubleJeopardyCategories?.forEach(cat => cat.clues.forEach(clue => allClueIds.add(clue.id)));

    const danglingDailyDoubles = (config.dailyDoubleClueIds ?? []).filter(id => !allClueIds.has(id));
    if (danglingDailyDoubles.length > 0) {
        errors.push('Daily Double selection includes clues that are no longer on the board.');
    }

    return errors;
}